<template>
  <div class="rounded-sm border">
    <loader :is-loading="resultsLoading" />
    <div class="flex w-full justify-between border-b p-6">
      <div class="flex items-center space-x-3">
        <back-button :route="route.fullPath" @click="emit('back')" />
        <lf-h4>{{ scorecard?.name || "-" }}</lf-h4>
        <scorecard-risk-tier-pill
          v-if="scorecard?.scorecard_result?.score_card_risk_tier"
          :risk-tier="scorecard?.scorecard_result?.score_card_risk_tier"
          readonly
        />
        <scorecard-status-pill
          v-if="scorecard?.scorecard_result?.score_card_status"
          :status="scorecard?.scorecard_result?.score_card_status"
          readonly
        />
      </div>
      <div>
        <scorecard-id-badge :model-value="scorecard?.id" />
      </div>
    </div>

    <div class="flex flex-row items-stretch">
      <div
        class="max-h-70-screen w-1/3 max-w-full space-y-2 overflow-y-scroll border-r p-6 xl:w-1/4"
      >
        <div
          v-for="scorecard in scorecards"
          :key="scorecard.id"
          class="flex max-w-full cursor-pointer flex-wrap items-center justify-between rounded-sm border p-3 hover:bg-disabled xl:flex-nowrap xl:overflow-x-auto"
          :class="{
            'border-primary bg-primary-background-active':
              scorecard.id === selectedScorecardId,
          }"
          @click="selectScorecard(scorecard.id)"
        >
          <div class="flex-auto text-headline xl:w-5/12 xl:flex-none">
            {{ scorecard.name }}
          </div>
          <div
            class="flex flex-auto items-center justify-end space-x-1 xl:w-7/12 xl:flex-none"
          >
            <div class="w-1/2 text-xxs" :class="{ 'text-right': isReport }">
              <icon-base
                v-if="isKnockedOut(scorecard)"
                :icon="IconKnockout"
                :color="UTIL_COLORS.WARNING"
              />
              {{
                $t("SCORECARDS.PTS", {
                  points: getScorecardResultPoints(scorecard),
                })
              }}
            </div>
            <div
              v-if="!isReport"
              class="flex w-1/2 items-center justify-between space-x-2"
            >
              <scorecard-risk-tier-pill
                v-if="scorecard.scorecard_result?.score_card_risk_tier"
                :risk-tier="scorecard.scorecard_result?.score_card_risk_tier"
                clas="flex-auto"
                readonly
                reduce
              />
              <scorecard-status-pill
                v-if="scorecard.scorecard_result?.score_card_status"
                :status="scorecard.scorecard_result?.score_card_status"
                class="flex-auto"
                readonly
                reduce
              />
            </div>
          </div>
        </div>
      </div>
      <div class="max-h-70-screen w-2/3 overflow-y-scroll xl:w-3/4">
        <div class="p-6">
          <scorecard-points-bar
            v-if="isReport"
            :categories="scorecard?.content?.categories"
            :total="scorecard?.points || 0"
            show-percentage
          />
          <scorecard-results-points-bar
            v-else
            :categories="scorecard?.scorecard_result?.categories || []"
            :total="scorecard?.points || 0"
            :started="!!scorecard?.scorecard_result"
            show-percentage
            :knocked-out="isKnockedOut(scorecard)"
          />
        </div>
        <lf-table
          class="sc-table"
          :columns="columns"
          :data="categories"
          :row-click-condition="toggleRow"
          :has-border="false"
          :full-height="false"
          :lazy-load="lazyLoadOptions"
          head-classes="border-t border-b"
        />
        <div
          v-if="categories.length"
          class="flex h-16 w-full items-center justify-between rounded-b bg-gray-100 p-6"
        >
          <lf-h4>
            {{ $t("SCORECARDS.TOTAL_PTS") }}
          </lf-h4>
          <lf-h4 v-if="scorecard">
            <icon-base
              v-if="isKnockedOut(scorecard)"
              :icon="IconKnockout"
              :color="UTIL_COLORS.WARNING"
            />
            {{
              $t("SCORECARDS.PTS", {
                points: getScorecardResultPoints(scorecard),
              })
            }}
          </lf-h4>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";

import { SERVICE_CUSTOM, UTIL_COLORS } from "@/helpers/constants";
import { getScorecardIsKnockedOut } from "@/helpers/scorecards";
import { useScorecardsServices } from "@/hooks/scorecards";

import ScorecardRiskTierPill from "../ScorecardRiskTierPill.vue";
import ScorecardStatusPill from "../ScorecardStatusPill.vue";
import IconKnockout from "@/components/icons/IconKnockout.vue";
import ScorecardIdBadge from "@/components/scorecards/ScorecardIdBadge.vue";
import ScorecardPointsBar from "@/components/scorecards/ScorecardPointsBar.vue";
import ScorecardResultsPointsBar from "@/components/scorecards/ScorecardResultsPointsBar.vue";
import Attribute from "@/components/scorecards/scorecardPreviewModal/tableComponents/groupScorecard/Attribute.vue";
import AttributePoints from "@/components/scorecards/scorecardPreviewModal/tableComponents/groupScorecard/AttributePoints.vue";
import DataProvider from "@/components/scorecards/scorecardPreviewModal/tableComponents/groupScorecard/DataProvider.vue";
import ScorecardKnockout from "@/components/scorecards/scorecardPreviewModal/tableComponents/groupScorecard/ScorecardKnockout.vue";
import ScorecardValueWithTooltip from "@/components/scorecards/scorecardPreviewModal/tableComponents/groupScorecard/ScorecardValueWithTooltip.vue";
import BackButton from "@/components/ui/buttons/BackButton.vue";
import LfTable from "@/components/ui/table/LfTable.vue";

import type { LazyLoadOptions } from "@/models/common";
import type {
  IScorecardContentCategory,
  IScorecardGroup,
  ScorecardGroupCategoryForPreview,
  ScorecardGroupPreview,
} from "@/models/scorecards";
import type { PropType } from "vue";

const emit = defineEmits<{
  (eventName: "back"): void;
  (eventName: "selectScorecard", scorecardId: string): void;
}>();

const props = defineProps({
  resultsLoading: {
    type: Boolean,
    default: false,
  },
  scorecardGroup: {
    type: Object as PropType<IScorecardGroup>,
    required: true,
  },
  scorecards: {
    type: Array as PropType<ScorecardGroupPreview[] | null>,
  },
  scorecard: {
    type: Object as PropType<ScorecardGroupPreview | null>,
  },
  selectedScorecardId: {
    type: String,
  },
  pageIsView: {
    type: Boolean,
    default: false,
  },
  isReport: {
    type: Boolean,
    default: false,
  },
  started: {
    type: Boolean,
    required: true,
  },
});

const { t } = useI18n();
const route = useRoute();

const { getServiceName, getServiceDescription, getServiceProviderDetails } =
  useScorecardsServices();

const lazyLoadOptions: LazyLoadOptions = {
  style: {},
};

const columns = [
  {
    label: t("SCORECARDS.ATTRIBUTES"),
    key: "attribute",
    component: Attribute,
  },
  {
    label: t("GENERAL.DATA_PROVIDERS"),
    key: "dataService",
    component: DataProvider,
  },
  {
    label: t("SCORECARDS.KNOCKOUT"),
    key: "knockout",
    component: ScorecardKnockout,
  },
  {
    label: t("SCORECARDS.VALUE"),
    headerClass: "text-right",
    columnClass: "text-right",
    key: "value",
    component: ScorecardValueWithTooltip,
  },
  {
    label: t("SCORECARDS.DISTRIBUTION"),
    headerClass: "text-right",
    columnClass: "text-right",
    key: "distribution_value",
  },
  {
    label: t("SCORECARDS.ATTRIBUTE_POINTS"),
    key: "points",
    headerClass: "text-right",
    columnClass: "text-right",
    component: !props.isReport ? AttributePoints : null,
  },
];

const isOpen = reactive<Record<string, boolean>>({});

const categories = computed(() => {
  const groupCategories = props.isReport
    ? props.scorecard?.content?.categories
    : props.scorecardGroup.results?.[0]?.score_card_results?.find(
        (scr) => scr.score_card_id === props.selectedScorecardId
      )?.categories || [];
  return (
    groupCategories?.map((category, index) => {
      return buildCategoryForTable(category, index, { withBackups: true });
    }) || []
  );
});

const buildCategoryForTable = (
  category: IScorecardContentCategory,
  index: number,
  backupOptions: {
    withBackups: boolean;
    backupApplied?: IScorecardContentCategory;
  }
): ScorecardGroupCategoryForPreview => {
  const results = props.scorecard?.scorecard_result?.categories.find(
    ({ attribute, context }) => {
      if (category.attribute === SERVICE_CUSTOM) {
        return context?.value?.id === category.context?.value?.id;
      }
      return attribute === category.attribute;
    }
  );
  const knockout = props.scorecard?.scorecard_result?.knockouts?.find(
    (knockout) => {
      if (category.attribute === SERVICE_CUSTOM) {
        return (
          knockout.category.context?.value?.id === category.context?.value?.id
        );
      }
      return knockout.category.attribute === category.attribute;
    }
  );
  const getValue = () => {
    if (backupOptions.withBackups) {
      return results?.attribute_value === null
        ? "null"
        : results?.attribute_value;
    }
    const result = getBackupApplied(
      category,
      backupOptions.backupApplied
    ).attribute_value;
    return result === null ? "null" : result;
  };
  return {
    ...category,
    index,
    attribute: getServiceName(
      String(category?.attribute || ""),
      category.context
    ),
    attributeOriginal: category?.attribute || "",
    info: getServiceDescription(
      String(category?.attribute || ""),
      category.context
    ),
    dataService: getServiceProviderDetails(String(category?.attribute || "")),
    results,
    calculated_points: backupOptions.withBackups
      ? results?.calculated_points
      : getBackupApplied(category, backupOptions.backupApplied)
          .calculated_points,
    knockout: !!category?.knockout,
    knockout_details: knockout,
    value: getValue(),
    distribution_value:
      Math.round((category.points / (props.scorecard?.points || 1)) * 100) +
      "%",
    children: backupOptions.withBackups
      ? category.backups?.map((backup) =>
          buildCategoryForTable(backup, index, {
            withBackups: false,
            backupApplied: category.backup_applied,
          })
        )
      : undefined,
    backup: backupOptions.withBackups ? false : true,
    open: !!category.attribute && isOpen[category.attribute],
  };
};

const getBackupApplied = (
  category: IScorecardContentCategory,
  backupApplied: IScorecardContentCategory | undefined
) => {
  return backupApplied ?? category;
};

const toggleRow = (row: ScorecardGroupCategoryForPreview) => {
  if (!row.attributeOriginal) {
    return;
  }
  isOpen[row.attributeOriginal] = !row.open;
};

const getScorecardResultPoints = (sc?: ScorecardGroupPreview) => {
  if (!sc) {
    return "-";
  }
  if (props.isReport) {
    return sc.points;
  }
  const resultPoints = isKnockedOut(sc)
    ? ""
    : sc.scorecard_result?.points || "-";
  const scorecardPoints = sc.points ?? 0;
  return `${resultPoints} ⁄ ${scorecardPoints}`;
};

const isKnockedOut = (sc?: ScorecardGroupPreview | null) => {
  const result = sc?.scorecard_result;
  return getScorecardIsKnockedOut(result);
};

const selectScorecard = (id: string) => {
  emit("selectScorecard", id);
};

const openBackupAppliedRows = () => {
  categories.value.forEach((category) => {
    if (category.backup_applied) {
      toggleRow(category);
    }
  });
};

onMounted(() => {
  openBackupAppliedRows();
});
</script>

<style scoped>
@reference "@/assets/tailwind.css";

.sc-table :deep(.child-row) {
  @apply bg-disabled/50;
}
</style>
